import { Controller, useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { Button, DialogActions, DialogContent, Stack, Typography } from '@mui/material';

import { API_ERRORS } from 'constants/api';
import { PASS_MIN_LENGTH } from 'constants/auth';
import { NOTIFICATION_TYPES } from 'constants/notificationTypes';
import { passwordValidationRules } from 'constants/passwordValidation';
import { formatFormValues } from 'helper/formatFormValues';
import { addNotification } from 'helper/notifications';
import { usePasswordValidation } from 'hooks/usePasswordValidation';
import { useDialog } from 'hooks/useDialog';
import { useChangePasswordMutation } from 'store/entities/users/api';
import { Input, PasswordValidationInfo } from 'components/simple';
import { Dialog } from 'components/complex';

const dialogId = 'password_update_dialog';

const styles = {
  actions: ({ breakpoints }) => ({
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: 3,
    paddingTop: 0,
    gap: 1,
    width: '100%',
    alignItems: 'stretch',

    [breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
  }),
  content: {
    paddingTop: 0,
    paddingBottom: 6,
  },
  formContent: {
    width: '100%',
    maxWidth: 700,
  },
};

const FORM_FIELDS = {
  OLD_PASSWORD: 'old_password',
  NEW_PASSWORD1: 'new_password1',
  NEW_PASSWORD2: 'new_password2',
};

const defaultValues = {
  [FORM_FIELDS.OLD_PASSWORD]: '',
  [FORM_FIELDS.NEW_PASSWORD1]: '',
  [FORM_FIELDS.NEW_PASSWORD2]: '',
};

export const PasswordUpdateDialog = () => {
  const [showPassword, toggleShowPassword] = useToggle();
  const { closeDialog } = useDialog();

  const [changePassword, { isLoading, error: apiError }] = useChangePasswordMutation();

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isValid },
  } = useForm({ defaultValues, mode: 'onChange' });

  const { passwordValidation, confirmPassError, isValidPasswordValues } = usePasswordValidation({
    control,
    dirtyFields,
    passFieldName: FORM_FIELDS.NEW_PASSWORD1,
    confirmPassFieldName: FORM_FIELDS.NEW_PASSWORD2,
  });

  const formInputsData = [
    {
      name: FORM_FIELDS.OLD_PASSWORD,
      rules: {
        required: 'Current password is required',
      },
      label: 'Current password',
      type: !showPassword ? 'password' : 'text',
      placeholder: 'Enter your password',
      required: true,
    },
    {
      name: FORM_FIELDS.NEW_PASSWORD1,
      rules: {
        required: 'Password is required',
        validate: passwordValidationRules,
      },
      label: 'New password',
      type: !showPassword ? 'password' : 'text',
      placeholder: 'Enter your password',
      required: true,
    },
    {
      name: FORM_FIELDS.NEW_PASSWORD2,
      label: 'Confirm new password',
      type: !showPassword ? 'password' : 'text',
      placeholder: 'Enter your password',
      required: true,
    },
  ];

  const getFieldError = (fieldName, formError) => {
    switch (fieldName) {
      case FORM_FIELDS.OLD_PASSWORD:
        return apiError
          ? {
              message: apiError.data?.old_password?.[0] || API_ERRORS.DEFAULT,
            }
          : formError;
      case FORM_FIELDS.NEW_PASSWORD2:
        return confirmPassError;
      default:
        return formError;
    }
  };

  const onCancel = () => {
    closeDialog();
  };

  const onSubmit = async (values) => {
    const { error } = await changePassword(formatFormValues(values));

    if (!error) {
      onCancel();
      addNotification('Your password has been changed successfully.', NOTIFICATION_TYPES.SUCCESS);
    }
  };

  return (
    <Dialog withCustomContent id={dialogId} title="Action is Required" onClose={onCancel} isLoading={isLoading}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={styles.content}>
          <Stack spacing={{ xs: 3, md: 5 }}>
            <Stack spacing={{ xs: 1, md: 2 }}>
              <Typography variant="body2">
                We have updated our password policy to be a minimum of {PASS_MIN_LENGTH} characters to ensure best
                practice. Your password is currently less than this and requires updating.
              </Typography>

              <Typography variant="body2">
                Please update your password so it meets the new requirement of being at least 12 characters.
              </Typography>
            </Stack>

            <Stack spacing={{ xs: 3, md: 4 }} sx={styles.formContent}>
              {formInputsData.map(({ name, rules, ...inputProps }) => (
                <Controller
                  key={name}
                  name={name}
                  rules={rules}
                  control={control}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => {
                    const fieldError = getFieldError(name, error);

                    return (
                      <Stack spacing={{ xs: 3, md: 4 }}>
                        <Input
                          {...field}
                          {...inputProps}
                          id={`${dialogId}-${name}`}
                          innerRef={ref}
                          error={fieldError}
                          inputButtonProps={{
                            showPassword,
                            onClick: toggleShowPassword,
                          }}
                        />

                        {name === FORM_FIELDS.NEW_PASSWORD1 && (
                          <PasswordValidationInfo passwordValidation={passwordValidation} prefix={dialogId} />
                        )}
                      </Stack>
                    );
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={styles.actions} disableSpacing>
          <Button onClick={onCancel} color="secondary" disabled={isLoading}>
            Cancel
          </Button>

          <Button type="submit" disabled={!isValidPasswordValues || !isValid} loading={isLoading}>
            Confirm
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
